"use client"

import type React from "react"
import { useEffect, useRef, useState } from "react"

interface WeightModalProps {
  isOpen: boolean
  productName: string
  pricePerKg: number
  availableWeight: number
  unitLabel: string
  onClose: () => void
  onSave: (weight: number) => void
}

const WeightModal: React.FC<WeightModalProps> = ({
  isOpen,
  productName,
  pricePerKg,
  availableWeight,
  unitLabel,
  onClose,
  onSave,
}) => {
  const [weight, setWeight] = useState<string>("")
  const [calculatedPrice, setCalculatedPrice] = useState<number>(0)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (isOpen) {
      setWeight("")
      setCalculatedPrice(0)
      setErrorMessage("")
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }
  }, [isOpen])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation()

    if (e.key === "Escape") {
      e.preventDefault()
      onClose()
    } else if (e.key === "F8") {
      e.preventDefault()
      if (inputRef.current) {
        inputRef.current.focus()
      }
    } else if (e.shiftKey) {
      e.preventDefault()
      const weightValue = Number.parseFloat(weight)
      if (weightValue > 0 && weightValue <= availableWeight) {
        onSave(weightValue)
      }
    }
  }

  const handleWeightChange = (value: string) => {
    // Permitir solo un punto decimal y números
    const sanitizedValue = value.replace(/[^0-9.]/g, "")
    const parts = sanitizedValue.split(".")
    if (parts.length > 2) {
      return // No permitir más de un punto decimal
    }

    const weightNumber = Number.parseFloat(sanitizedValue) || 0
    if (weightNumber > availableWeight) {
      setErrorMessage(
        `El producto tiene disponible ${availableWeight.toFixed(1)} ${unitLabel}, pero intentas ingresar ${weightNumber.toFixed(1)} ${unitLabel}.`,
      )
    } else {
      setErrorMessage("")
      setWeight(sanitizedValue)
      setCalculatedPrice(Math.round(weightNumber * pricePerKg))
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all">
        {/* Cabecera */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-600 p-4 text-white">
          <h3 className="text-xl font-bold">{productName}</h3>
          <p className="text-white/80 mt-1 truncate">Producto por {unitLabel}</p>
        </div>

        {/* Cuerpo */}
        <div className="p-5 space-y-5">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Cantidad ({unitLabel}):</label>
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={weight}
                onChange={(e) => handleWeightChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ingrese el valor"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">{unitLabel}</div>
            </div>
          </div>

          {errorMessage && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <i className="fas fa-exclamation-circle mr-2"></i>
              {errorMessage}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-center justify-between">
            <span className="text-blue-800 font-medium">Valor Total:</span>
            <span className="text-2xl font-bold text-blue-700">${calculatedPrice.toLocaleString()}</span>
          </div>

          <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-sm text-gray-600">
            <i className="fas fa-info-circle mr-2 text-blue-500"></i>
            Precio por {unitLabel}: ${pricePerKg.toLocaleString()}
          </div>
        </div>

        {/* Pie de página */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 
                     transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            <i className="fas fa-times-circle mr-2"></i>
            Cancelar
          </button>

          <button
            onClick={() => {
              const weightValue = Number.parseFloat(weight)
              if (weightValue > 0 && weightValue <= availableWeight) {
                onSave(weightValue)
              }
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 
                      ${
                        !errorMessage && weight
                          ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 focus:ring-blue-500"
                          : "bg-gray-200 text-gray-500 cursor-not-allowed"
                      }`}
            disabled={!!errorMessage || !weight}
          >
            <i className="fas fa-check-circle mr-2"></i>
            Guardar
          </button>
        </div>

        {/* Guía de atajos */}
        <div className="px-4 py-2 bg-gray-100 border-t border-gray-200 flex justify-center gap-6">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-700 text-white text-xs font-bold rounded">F8</span>
            <span className="text-sm text-gray-600">Foco</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-700 text-white text-xs font-bold rounded">ESC</span>
            <span className="text-sm text-gray-600">Cancelar</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-700 text-white text-xs font-bold rounded">SHIFT</span>
            <span className="text-sm text-gray-600">Guardar</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WeightModal
